import React, { useState } from "react";
import { makeStyles } from "@mui/styles";

import MDCButton from "../../core/MDCButton";
import MDCCheckbox from "../../core/MDCCheckbox";
import MDCLabel from "../../core/MDCLabel";
import MDCText from "../../core/MDCText";
import DiscardIcon from "../../iconComponents/DiscardIcon";
import EditIcon from "../../iconComponents/EditIcon";
import { formatDate } from "../../../helpers/utils";

import theme from "../../../themes/mdc-theme";
import { JP_SIGNING_CONSTANTS } from "../../../constants/SigningProviderConstants";
import ExportPDFIcon from "../../iconComponents/ExportPDFIcon";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  signerLayout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 2.1fr) minmax(18rem, 1.2fr)",
    gap: "1rem",
    alignItems: "stretch",
  },

  title: {
    fontWeight: 400,
    fontSize: "1.25rem",
    marginBottom: ".5rem",
  },

  signerGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: "2.5rem",
    rowGap: ".25rem",
  },
  nonSignerHeading: {
    textAlign: "left",
    font: "normal normal bold 10px/16px Fira Sans",
    letterSpacing: "0px",
    color: theme.palette.MDCColors.textColor,
  },
  nonSignerSubHeading: {},
  nonSignerSubHeading: {
    textAlign: "left",
    font: "normal normal normal 10px/16px Fira Sans",
    letterSpacing: "0px",
    color: "theme.palette.MDCColors.textColor",
  },

  proxyPanel: {
    height: "100%",
    boxSizing: "border-box",
    padding: "1rem",
    border: `1px solid ${theme.palette.MDCColors.color21}`,
    borderRadius: "3px",
    "& .MuiFormControlLabel-root": {
      margin: 0,
    },
    "& .MuiCheckbox-root": {
      padding: 0,
      marginRight: ".25rem",
    },
    "& .MuiTypography-root.MuiFormControlLabel-label": {
      marginLeft: ".25rem",
      fontSize: ".875rem",
      fontWeight: 700,
    },
  },

  signerRowContent: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    padding: "0.4rem 0",
  },

  representativeLabel: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },

  representativeName: {
    fontWeight: 700,
    lineHeight: 1.2,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    textAlign: "left",
    font: "normal normal 600 14px/17px Fira Sans",
    letterSpacing: "0px",
    color: theme.palette.MDCColors.textColor,
    opacity: 1,
  },
  disabledSigner: {
    opacity: 0.5,
  },

  representativeBirthDate: {
    textAlign: "left",
    font: "normal normal normal 13px/15px Fira Sans",
    letterSpacing: "0px",
    color: theme.palette.MDCColors.color18,
    opacity: 1,
  },

  signerContent: {
    marginLeft: "2rem",
    marginTop: ".5rem",
    width: "calc(100% - 2rem)",
  },

  sectionTitle: {
    fontWeight: 600,
    fontSize: ".75rem",
    lineHeight: 1.35,
    marginBottom: ".375rem",
  },

  actions: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "2rem",
  },

  selectedProvider: {
    border: "1px solid #dbe3f0",
    borderRadius: "4px",
    padding: "12px",
    marginBottom: "16px",
    backgroundColor: "#fafcff",
  },
  error: { color: theme.palette.MDCColors.errorDanger, marginTop: ".75rem" },
  emailError: {
    color: theme.palette.MDCColors.errorDanger,
    fontSize: ".75rem",
    marginTop: ".25rem",
  },
  muted: {
    textAlign: "left",
    font: "normal normal bold 10px/16px Fira Sans",
    letterSpacing: "0px",
    color: theme.palette.MDCColors.textColor,
  },
  heading: {
    textAlign: "left",
    font: "normal normal normal 10px/16px Fira Sans",
    letterSpacing: "0px",
    color: theme.palette.MDCColors.textColor,
  },
  signerRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(12rem, 18rem)",
    gap: "1rem",
    alignItems: "center",
    padding: ".5rem 0",
    borderBottom: `1px solid ${theme.palette.MDCColors.color21}`,
  },
  signerCard: {
    height: "100%",
    boxSizing: "border-box",
    padding: "1rem",
    border: `1px solid ${theme.palette.MDCColors.color21}`,
  },
  signerList: {
    maxHeight: "20rem",
    overflowY: "auto",
    paddingRight: ".5rem",
  },
  proxySignerList: {
    maxHeight: "20rem",
    overflowY: "auto",
    marginTop: "1rem",
    paddingRight: ".5rem",
  },
  proxySignerCard: {
    marginTop: "1.25rem",
  },
  proxySignerMeta: {
    marginTop: ".125rem",
    fontSize: ".75rem",
  },
  proxySignerEmailRow: {
    display: "flex",
    alignItems: "center",
    gap: ".625rem",
    marginTop: ".5rem",
  },
  proxySignerEmail: {
    flex: 1,
    minWidth: 0,
    padding: ".375rem .75rem",
    background: theme.palette.MDCColors.color19,
    borderRadius: "3px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  proxySignerActions: {
    display: "flex",
    alignItems: "center",
    gap: ".5rem",
    color: theme.palette.MDCColors.color23,
  },
  proxySignerIcon: { cursor: "pointer", lineHeight: 0 },
  proxyAddButton: {
    "&&": {
      minWidth: 0,
      padding: ".25rem .5rem",
      fontSize: ".75rem",
    },
  },
}));

const formatBirthDate = (date) => {
  return formatDate(date);
};

const SignerSelectionPanel = ({
  isDetailDialog,
  representatives,
  proxySigners,
  flowError,
  selectRepresentative,
  updateRepresentative,
  openProxySignerModal,
  editProxySigner,
  removeProxySigner,
  downloadProxyDocument,
  goBack,
  continueToSigning,
  selectSignersHelp,
  selectSignersHeading,
  disabled = false,
}) => {
  const localClasses = useStyles();
  const [emailErrors, setEmailErrors] = useState({});

  /**
   * Submit selected signers for backend validation.
   */
  const handleContinue = () => {
    setEmailErrors({});
    continueToSigning(setEmailErrors);
  };

  const hasSignerWithEmail =
    representatives.some(
      (representative) =>
        representative.selected &&
        !representative.disabled &&
        Boolean(representative.email?.trim())
    ) || proxySigners.some((proxySigner) => Boolean(proxySigner.email?.trim()));

  return (
    <div className={localClasses.root}>
      <div className={localClasses.signerLayout}>
        <div className={localClasses.signerCard}>
          <p className={localClasses.muted}>
            {selectSignersHelp}{" "}
            <span className={localClasses.heading}>{selectSignersHeading}</span>
          </p>

          <div className={localClasses.signerList}>
            <div className={localClasses.signerGrid}>
              {representatives.map((representative) => (
                <div
                  className={`${localClasses.signerRowContent} ${
                    representative.disabled ? localClasses.disabledSigner : ""
                  }`}
                  key={representative.id}
                >
                  <MDCCheckbox
                    label={
                      <span className={localClasses.representativeLabel}>
                        <span className={localClasses.representativeName}>
                          {representative.name ||
                            `Representative ${representative.id}`}
                        </span>
                        {representative.birthDate && (
                          <span
                            className={localClasses.representativeBirthDate}
                          >
                            {formatBirthDate(representative.birthDate)}
                          </span>
                        )}
                      </span>
                    }
                    name={representative.id}
                    checked={representative.selected}
                    disabled={representative.disabled || isDetailDialog}
                    onChange={(event) => {
                      const selected = event.target.checked;
                      selectRepresentative(representative.id, selected);
                      if (!selected) {
                        setEmailErrors((current) => ({
                          ...current,
                          [representative.id]: "",
                        }));
                      }
                    }}
                  />
                  {representative.selected && !representative.disabled && (
                    <div className={localClasses.signerContent}>
                      <MDCText
                        name={`${representative.id}-email`}
                        type="email"
                        value={representative.email}
                        placeholder="E-Mail"
                        onChange={(event) => {
                          const value = event.target.value;
                          updateRepresentative(representative.id, {
                            email: value,
                          });
                          setEmailErrors((current) => ({
                            ...current,
                            [representative.id]: "",
                          }));
                        }}
                        error={!!emailErrors[representative.id]}
                        disabled={isDetailDialog}
                      />
                      {emailErrors[representative.id] && (
                        <div className={localClasses.emailError}>
                          {emailErrors[representative.id]}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className={localClasses.proxyPanel}>
          <p className={localClasses.nonSignerHeading}>
            Die gewünschte Person ist nicht dabei?{" "}
            <span className={localClasses.nonSignerSubHeading}>
              Sie können eine zeichnungsberechtigte Person hinzufügen, jedoch
              müssen Sie für diese Person eine Vollmacht hochladen.
            </span>
          </p>
          <MDCButton
            variant="outlined"
            label={JP_SIGNING_CONSTANTS.ADD_NON_REPRESENTATIVE}
            className={localClasses.proxyAddButton}
            onClick={openProxySignerModal}
            disabled={disabled || isDetailDialog}
          />
          <div className={localClasses.proxySignerList}>
            {proxySigners.map((proxySigner) => (
              <div
                className={localClasses.proxySignerCard}
                key={proxySigner.id}
              >
                <MDCCheckbox
                  label={`${proxySigner.firstName} ${proxySigner.lastName}`}
                  name="proxy-signer"
                  checked
                  onChange={() => {}}
                />
                <div className={localClasses.proxySignerMeta}>
                  {formatDate(proxySigner.birthDate)}
                  {proxySigner.onBehalfOfName &&
                    ` im Auftrag von: ${proxySigner.onBehalfOfName}`}
                </div>
                <div className={localClasses.proxySignerEmailRow}>
                  <div className={localClasses.proxySignerEmail}>
                    <MDCLabel label={proxySigner.email} isReadOnly />
                  </div>
                  <div className={localClasses.proxySignerActions}>
                    <div
                      className={localClasses.proxySignerIcon}
                      onClick={() => downloadProxyDocument(proxySigner)}
                    >
                      <ExportPDFIcon color={theme.palette.MDCColors.color23} />
                    </div>
                    <div
                      className={localClasses.proxySignerIcon}
                      onClick={() => removeProxySigner(proxySigner)}
                    >
                      <DiscardIcon />
                    </div>
                    <div
                      className={localClasses.proxySignerIcon}
                      onClick={() => editProxySigner(proxySigner)}
                    >
                      <EditIcon />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {flowError && <div className={localClasses.error}>{flowError}</div>}
      <div className={localClasses.actions}>
        <MDCButton
          variant="outlined"
          label="Zurück"
          onClick={goBack}
          disabled //always disable otherwise {disabled || isDetailDialog}
        />
        <MDCButton
          variant="contained"
          label="Weiter"
          onClick={handleContinue}
          disabled={disabled || isDetailDialog || !hasSignerWithEmail}
        />
      </div>
    </div>
  );
};

export default SignerSelectionPanel;
